import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { api } from '../lib/api';

export type Suggestion = {
  department_id: number;
  department_code: string;
  department_name: string;
  confidence: number;
};

export function AssignDropdown({
  suggestions,
  onAssign,
  disabled = false,
}: {
  suggestions: Suggestion[];
  onAssign: (department_id: number) => void;
  disabled?: boolean;
}) {
  const [showOther, setShowOther] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: deptList } = useQuery({
    queryKey: ['departments-lookup'],
    queryFn: () => api.listDepartments(),
    staleTime: 5 * 60_000,
  });

  // Close on outside click or Escape
  useEffect(() => {
    if (!showOther) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setShowOther(false);
    }
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowOther(false);
      }
    }
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [showOther]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {suggestions.map((s) => (
        <button
          key={s.department_id}
          disabled={disabled}
          onClick={() => onAssign(s.department_id)}
          className="rounded border border-info/40 bg-info/10 px-3 py-1 text-sm font-medium text-info hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
        >
          {s.department_code} {Math.round(s.confidence * 100)}%
        </button>
      ))}
      <div className="relative" ref={containerRef}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setShowOther((v) => !v)}
          className="inline-flex items-center gap-1 rounded border border-border bg-card px-3 py-1 text-sm text-foreground hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
        >
          {showOther ? 'Cancel' : suggestions.length > 0 ? 'Other' : 'Pick department'}
          <ChevronDown size={12} strokeWidth={1.5} />
        </button>
        {showOther && deptList && (
          <div className="absolute right-0 z-10 mt-1 max-h-64 w-56 origin-top overflow-auto rounded border border-border bg-card shadow-lg transition-all duration-150">
            {deptList.rows.map((d) => (
              <button
                key={d.id}
                onClick={() => {
                  onAssign(d.id);
                  setShowOther(false);
                }}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              >
                <span className="font-mono text-muted-foreground">{d.code}</span>
                <span className="ml-2 text-foreground">{d.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
